"use client"

import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { getCoins, getRoutes, swap, clearRoutes } from "../redux/slices/rangoSlice"

import SwapHeader from "../components/rango/SwapHeader"
import SwapForm from "../components/rango/SwapForm"
import BlockchainSelector from "../components/rango/BlockchainSelector"
import TokenSelector from "../components/rango/TokenSelector"
import RouteDisplay from "../components/rango/RouteDisplay"
import SettingsModal from "../components/rango/SettingsModal"
import ConfirmSwapModal from "../components/rango/ConfirmSwapModal"
import { ProcessingNotificationModal } from "../components/NotificationModal"

import WalletDashboardCard from "../components/WalletDashboardCard"
import QuickExchangeInfoCard from "../components/kaxaa-wallet/QuickExchangeInfoCard"
import RangoTransactionHistory from "../components/rango/RangoTransactionHistory"
import { useAuth } from "../context/AuthContext"
import { useTour } from "../hooks/useTour"



function RangoExchange({ onSend, onReceive, onUnshield }) {
  // State management
  const dispatch = useDispatch();
  const auth = useAuth();
  console.log("Auth user:", auth.user.wallet.publicWalletAddress);
  const [selectBlockchain, setSelectBlockchain] = useState(false)
  const [selectToken, setSelectToken] = useState(false)
  const [rangoSettings, setRangoSettings] = useState(false)
  const [widgetState, setWidgetState] = useState("SelectSwapPair")
  const [amount, setAmount] = useState(0)
  const [slippage, setSlippage] = useState("1")
  const [errorMessage, setErrorMessage] = useState("")
  const [actionButtonLabel, setActionButtonLabel] = useState("Find Multi-Swap Routes")
  const [amountError, setAmountError] = useState("")
  const [apiCallInProgress, setApiCallInProgress] = useState(false)
  const [loading, setLoading] = useState(true);
  const [showProcessingNotification, setShowProcessingNotification] = useState(false);
  const [showRouteHint, setShowRouteHint] = useState(false);
  const [receivingAmount, setReceivingAmount] = useState(0);
  const coins = useSelector(state => state.rango.coins);
  const routes = useSelector(state => state.rango.routes);
  const error = useSelector(state => state.rango.error);
  const { startTour } = useTour();


  useEffect(() => {
    document.title = "Rango Exchange - KAXAA Wallet"
    dispatch(getCoins());
  }, [dispatch]);

  useEffect(() => {
    if (coins[0]) {
      console.log("Rango coins loaded:", coins);
      setBlockChainSellSelected(coins.find(c => c.symbol === "POLYGON"));
      setBlockChainBuySelected(coins.find(c => c.symbol === "POLYGON"));

      setTokenSellSelected(coins.find(c => c.symbol === "POLYGON").coins.find(t => t.symbol === "USDC"));
      setTokenBuySelected(coins.find(c => c.symbol === "POLYGON").coins.find(t => t.symbol === "KAX"));
      setLoading(false)
    }
  }, [coins, loading]);

  useEffect(() => {
    if (!loading && coins.length > 0) {
      const timer = setTimeout(() => {
        startTour(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loading, coins, startTour]);


  // Default selections
  const [blockChainSellSelected, setBlockChainSellSelected] = useState({
    image: "https://api.rango.exchange/blockchains/polygon.svg",
    name: "POLYGON",
    displayName: "Polygon",
    type: "EVM",
    symbol: "POLYGON"
  })

  const [blockChainBuySelected, setBlockChainBuySelected] = useState({
    image: "https://api.rango.exchange/blockchains/avax_cchain.svg",
    name: "AVAX_CCHAIN",
    displayName: "Avalanche",
    type: "EVM",
    symbol: "AVAX_CCHAIN"
  })

  const [tokenSellSelected, setTokenSellSelected] = useState({
    image: "https://api.rango.exchange/tokens/POLYGON/KAX.png",
    symbol: "KAX",
    address: "0x42af7aac6ae6425ffa96370cfd0b12522aa4b458",
  })

  const [tokenBuySelected, setTokenBuySelected] = useState({
    image: "https://api.rango.exchange/i/kX4edQ",
    symbol: "AVAX",
    address: null,
  })

  const [blockChainSelectionContext, setBlockChainSelectionContext] = useState("sell")
  const [tokenSelectionContext, setTokenSelectionContext] = useState("sell")
  const [blockChainSearchKeyword, setBlockChainSearchKeyword] = useState("")
  const [tokenSearchKeyword, setTokenSearchKeyword] = useState("")
  const [selectedRoute, setSelectedRoute] = useState(null)

  // Helper functions
  const selectBlockChain = (blockchain) => {
    if (blockChainSelectionContext === "sell") {
      setBlockChainSellSelected(blockchain)
      setTokenSellSelected({
        image: "",
        symbol: "Select Token",
        address: null,
      })
    } else {
      setBlockChainBuySelected(blockchain)
      setTokenBuySelected({
        image: "",
        symbol: "Select Token",
        address: null,
      })
    }
    setWidgetState("SelectSwapPair")
    setShowRouteHint(false)
    setSelectBlockchain(false)
    setActionButtonLabel("Find Routes")
    setErrorMessage("")
    setReceivingAmount(0)
    dispatch(clearRoutes())
  }

  const selectTokenClick = (token) => {
    if (tokenSelectionContext === "sell") {
      setTokenSellSelected(token)
    } else {
      setTokenBuySelected(token)
    }
    setSelectToken(false)
    setWidgetState("SelectSwapPair")
    setShowRouteHint(false)
    setActionButtonLabel("Find Routes")
    setReceivingAmount(0)
    setErrorMessage("")
    dispatch(clearRoutes())
  }

  const findRoute = async () => {
    setErrorMessage("")
    setAmountError("")
    if (amount <= 0) {
      setAmountError("Please enter a valid amount")
      return
    }
    if (tokenSellSelected.symbol === "Select Token" || tokenBuySelected.symbol === "Select Token") {
      setErrorMessage("Please select both tokens")
      return
    }
    if (tokenSellSelected.address === tokenBuySelected.address && blockChainSellSelected.symbol === blockChainBuySelected.symbol) {
      setErrorMessage("Please select different tokens for swap")
      return
    }
    if (parseFloat(amount) > parseFloat(tokenSellSelected.balance)) {
      setAmountError("Insufficient balance for the swap")
      return
    }

    // Set loading state for find routes
    setApiCallInProgress(true)
    setActionButtonLabel("Finding Routes...")

    await dispatch(getRoutes({
      from: {
        blockchain: blockChainSellSelected.symbol,
        address: tokenSellSelected.address,
        symbol: tokenSellSelected.symbol,
      },
      to: {
        blockchain: blockChainBuySelected.symbol,
        address: tokenBuySelected.address,
        symbol: tokenBuySelected.symbol,
      },
      amount
    }))

    // Reset loading state after routes are found
    setApiCallInProgress(false)
    setActionButtonLabel("Find Routes")
  }

  const swapWithService = (route) => {
    setSelectedRoute(route)
    setWidgetState("ConfirmSwap")
    setActionButtonLabel("Confirm Swap")
  }

  const actionButtonHandler = async () => {
    if (apiCallInProgress) return

    if (widgetState === "SelectSwapPair") {
      setShowRouteHint(true)
      findRoute()
    } else if (widgetState === "ShowRoute") {
      setWidgetState("ConfirmSwap")
      setActionButtonLabel("Confirm Swap")
    } else if (widgetState === "ConfirmSwap") {
      setApiCallInProgress(true)
      setActionButtonLabel("Processing...")

      const result = await dispatch(swap(selectedRoute));
      setApiCallInProgress(false)

      if (result.error) {
        setErrorMessage(result.error.message || "Swap failed. Please try again.")
        setActionButtonLabel("Confirm Swap")
      } else {
        setWidgetState("SwapSuccess")
        setActionButtonLabel("Swap Successful")
        // Show notification modal only after successful API response
        setShowProcessingNotification(true)
        // Optionally reset the widget after a successful swap
        resetWidget()
      }

    }
  }

  const getFilteredTokens = () => {
    const selectedChain = tokenSelectionContext === "sell" ? blockChainSellSelected : blockChainBuySelected
    return selectedChain.coins || []
  }

  const resetWidget = () => {
    setWidgetState("SelectSwapPair")
    setErrorMessage("")
    setAmount(0)
    setSelectedRoute(null)
    setShowRouteHint(false)
    setReceivingAmount(0)
    dispatch(clearRoutes())
    if (coins[0]) {
      setBlockChainSellSelected(coins.find(c => c.symbol === "POLYGON"));
      setBlockChainBuySelected(coins.find(c => c.symbol === "POLYGON"));

      setTokenSellSelected(coins.find(c => c.symbol === "POLYGON").coins.find(t => t.symbol === "USDC"));
      setTokenBuySelected(coins.find(c => c.symbol === "POLYGON").coins.find(t => t.symbol === "KAX"));
      setLoading(false)
    }
    setActionButtonLabel("Find Routes")
  }

  // Calculate receiving amount based on token prices
  useEffect(() => {
    if (!amount || amount <= 0 || !tokenSellSelected?.price?.price || !tokenBuySelected?.price?.price) {
      setReceivingAmount(0)
      return
    }

    const sellPrice = parseFloat(tokenSellSelected.price.price)
    const buyPrice = parseFloat(tokenBuySelected.price.price)
    const sendAmount = parseFloat(amount)

    if (sellPrice > 0 && buyPrice > 0 && sendAmount > 0) {
      const estimatedReceiving = (sendAmount * sellPrice) / buyPrice
      setReceivingAmount(estimatedReceiving.toFixed(6))
    } else {
      setReceivingAmount(0)
    }
  }, [amount, tokenSellSelected, tokenBuySelected])

  useEffect(() => {
    // Handle routes response
    if (routes.length > 0) {
      setWidgetState("ShowRoute")
      setActionButtonLabel("Confirm Route")
    }
    // Handle error message when routes are fetched
    if (error) {
      setErrorMessage(error)
      setApiCallInProgress(false)
      setActionButtonLabel("Find Routes")
    }
  }, [routes, error])

  return (
    <>
      <main className="wallet-dashboard main-page">
        <div className="rango-exchange-page">
          <div className="wallet-dashboard p-0">
            <div className="grid lg:grid-cols-[450px_1fr] grid-cols-1 gap-[20px] items-start">
              <div className="flex flex-col">
                <WalletDashboardCard />
                <div className="hidden lg:block">
                  <QuickExchangeInfoCard />
                </div>
              </div>
              <div>
                <div id="tour-swap-container" className={` ${(widgetState === "ConfirmSwap" || selectBlockchain === true || selectToken === true || rangoSettings === true) ? '' : 'rango-ui'} relative d-flex flex-column justify-between`}>

                  <div className="d-flex flex-column rango-flex--between">
                    <div>
                      <SwapHeader resetWidget={resetWidget} setRangoSettings={setRangoSettings} />
                      {coins.length === 0 ? <div
                        className="d-flex justify-center align-center"
                      // style={{ height: "200px" }}
                      >
                        <div className="w-full">
                          <div className="rango-you--sell mb-3">
                            <div className="skeleton flex-1 mb-3" style={{ maxWidth: '31%', height: '20px', borderRadius: '6px' }}></div>
                            <div className="flex items-center gap-4">
                              <div className="skeleton flex-1" style={{ height: '48px', borderRadius: '8px' }}></div>
                              <div className="skeleton flex-1" style={{ height: '48px', borderRadius: '8px' }}></div>
                              <div className="skeleton flex-1" style={{ height: '48px', borderRadius: '8px' }}></div>
                            </div>
                          </div>
                          <div className="rango-you--receive">
                            <div className="skeleton flex-1 mb-3" style={{ maxWidth: '31%', height: '20px', borderRadius: '6px' }}></div>
                            <div className="flex items-center gap-4">
                              <div className="skeleton flex-1" style={{ height: '48px', borderRadius: '8px' }}></div>
                              <div className="skeleton flex-1" style={{ height: '48px', borderRadius: '8px' }}></div>
                              <div className="skeleton flex-1" style={{ height: '48px', borderRadius: '8px' }}></div>
                            </div>
                          </div>
                        </div>
                      </div> : (
                        <SwapForm
                          blockChainSellSelected={blockChainSellSelected}
                          blockChainBuySelected={blockChainBuySelected}
                          tokenSellSelected={tokenSellSelected}
                          tokenBuySelected={tokenBuySelected}
                          amount={amount}
                          setAmount={setAmount}
                          setBlockChainSelectionContext={setBlockChainSelectionContext}
                          setSelectBlockchain={setSelectBlockchain}
                          setTokenSelectionContext={setTokenSelectionContext}
                          setSelectToken={setSelectToken}
                          setBlockChainSearchKeyword={setBlockChainSearchKeyword}
                          setTokenSearchKeyword={setTokenSearchKeyword}
                          setActionButtonLabel={setActionButtonLabel}
                          setWidgetState={setWidgetState}
                          setErrorMessage={setErrorMessage}
                          receivingAmount={receivingAmount}
                          amountError={amountError}
                          handleCalculate={findRoute}
                        />
                      )}

                      {errorMessage && (
                        <div className="text-danger mt-3" role="alert">
                          {typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)}
                        </div>
                      )}
                    </div>

                    {(widgetState !== "ConfirmSwap" && selectBlockchain === false && selectToken === false && rangoSettings === false) &&
                      <>
                        {widgetState !== "ShowRoute" && (
                          <div>
                            <button
                              id="tour-find-routes"
                              onClick={actionButtonHandler}
                              className={`${apiCallInProgress ? "rango-connect--wallet-disabled" : "rango-connect--wallet"}`}
                              disabled={apiCallInProgress}
                            >
                              {apiCallInProgress && (
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              )}
                              {actionButtonLabel && <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" className="me-2" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="m21 21l-4.34-4.34" /><circle cx="11" cy="11" r="8" /></g></svg>}
                              {actionButtonLabel}
                            </button>
                          </div>
                        )}

                        {showRouteHint && widgetState === "ShowRoute" && (
                          <div className="my-3 font-semibold text-14 lg:w-11/12">
                            Kaxaa system calculates the optimal routes that yields the maximum final amount of the desired token. Please select one of the routes:
                          </div>
                        )}

                        {routes.length ? (
                          <RouteDisplay
                            routes={routes}
                            swapWithService={swapWithService}
                            apiCallInProgress={apiCallInProgress}
                          />
                        ) : (
                          widgetState === "ShowRoute" && <div>No routes found. Please adjust your swap details.</div>
                        )}
                      </>
                    }
                  </div>

                  <BlockchainSelector
                    selectBlockchain={selectBlockchain}
                    setSelectBlockchain={setSelectBlockchain}
                    blockChainSearchKeyword={blockChainSearchKeyword}
                    setBlockChainSearchKeyword={setBlockChainSearchKeyword}
                    staticBlockchains={coins}
                    selectBlockChain={selectBlockChain}
                    blockChainSelectionContext={blockChainSelectionContext}
                    blockChainSellSelected={blockChainSellSelected}
                    blockChainBuySelected={blockChainBuySelected}
                  />

                  <TokenSelector
                    selectToken={selectToken}
                    setSelectToken={setSelectToken}
                    tokenSelectionContext={tokenSelectionContext}
                    tokenSearchKeyword={tokenSearchKeyword}
                    setTokenSearchKeyword={setTokenSearchKeyword}
                    getFilteredTokens={getFilteredTokens}
                    selectTokenClick={selectTokenClick}
                    tokenSellSelected={tokenSellSelected}
                    tokenBuySelected={tokenBuySelected}
                  />

                  <SettingsModal
                    rangoSettings={rangoSettings}
                    setRangoSettings={setRangoSettings}
                    slippage={slippage}
                    setSlippage={setSlippage}
                  />
                  {selectedRoute && (
                    <ConfirmSwapModal
                      widgetState={widgetState}
                      setWidgetState={setWidgetState}
                      setActionButtonLabel={setActionButtonLabel}
                      apiCallInProgress={apiCallInProgress}
                      amount={selectedRoute?.swaps[0]?.fromAmount || amount}
                      tokenSellSelected={tokenSellSelected}
                      blockChainSellSelected={blockChainSellSelected}
                      tokenBuySelected={tokenBuySelected}
                      blockChainBuySelected={blockChainBuySelected}
                      selectedRoute={selectedRoute}
                      actionButtonHandler={actionButtonHandler}
                      actionButtonLabel={actionButtonLabel}
                      address={auth.user.wallet.publicWalletAddress}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4">
              <RangoTransactionHistory />
            </div>
          </div>
        </div>
      </main>

      {/* Processing Notification Modal */}
      <ProcessingNotificationModal
        show={showProcessingNotification}
        onHide={() => setShowProcessingNotification(false)}
      />
    </>
  )
}


export default RangoExchange
